/* Iceberg-ui */
import { Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { ExpandMore } from '@mui/icons-material';
import {
    Box,
    CircularProgress,
    FormControl,
    FormLabel,
    MenuItem,
    Select
} from '@mui/material';
import { TooltipQ } from 'application/Shared/Components/Tooltips';

/* Shared */
import { Response } from 'application/Shared/Helpers';

/*  NPM  */
import axios from 'axios';

/* React */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProviderType } from '../../../types/tenantProvider';

const { getData, hasData } = Response;

export const AzureTenantSelector = ({
    accessToken,
    onSelectTenant,
    filterExistingSyncedTenants = true
}) => {
    const [identityTenantIds, setIdentityTenantIds] = useState([]);
    const [loadedTenants, setLoadedTenants] = useState(false);
    const [azureTenants, setAzureTenants] = useState([]);
    const [loadedAzureTenants, setLoadedAzureTenants] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        async function getTenants() {
            if (filterExistingSyncedTenants) {
                const response = await axios.get('/clientLink', {
                    params: {
                        hidden: false,
                        includeProvider: true,
                        includeTenant: true,
                        size: -1
                    }
                });

                if (hasData(response)) {
                    const data = getData(response);

                    const tenantData = data.reduce((acc, tenantLink) => {
                        // Only active Microsoft Entra ID integrations
                        const microsoftProvider = tenantLink?.provider?.find(
                            (provider) =>
                                provider.providerType === ProviderType.Microsoft
                        );
                        if (microsoftProvider?.enabled) {
                            acc.push(microsoftProvider.identityTenantId);
                        }
                        return acc;
                    }, []);
                    setIdentityTenantIds(tenantData);
                }
            }
            setLoadedTenants(true);
        }

        getTenants();
    }, [filterExistingSyncedTenants]);

    useEffect(() => {
        async function getAzureTenants() {
            if (accessToken && loadedTenants && !loadedAzureTenants) {
                const api = axios.create({
                    baseURL:
                        'https://management.azure.com/tenants?api-version=2020-01-01',
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                });

                const response = await api.get();
                if (response?.data?.value?.length) {
                    const { value } = response.data;

                    // Filter out any Microsoft Entra ID tenants that we have already integrated.
                    const filteredAzureTenants = value.filter(
                        (at) => !identityTenantIds.includes(at.tenantId)
                    );

                    setAzureTenants(filteredAzureTenants);

                    const firstTenant = filteredAzureTenants[0];
                    if (firstTenant) {
                        setSelectedTenant(firstTenant);
                        onSelectTenant(firstTenant);
                    }
                }

                setLoadedAzureTenants(true);
            }
        }

        getAzureTenants();
    }, [
        accessToken,
        onSelectTenant,
        loadedTenants,
        loadedAzureTenants,
        identityTenantIds
    ]);

    if (loadedTenants && loadedAzureTenants) {
        return (
            <FormControl style={{ width: '100%' }}>
                <Flex mb="24px">
                    <Box width="40%" display="inline-flex" alignItems="center">
                        <FormLabel component="label" className="anmForm">
                            {t('Provider.MicrosoftConfiguration.tenant')}
                        </FormLabel>
                        <TooltipQ
                            text={t(
                                'Provider.MicrosoftConfiguration.tenantSelector.tooltip'
                            )}
                        />
                    </Box>
                    <Box width="60%">
                        <Select
                            IconComponent={ExpandMore}
                            variant="outlined"
                            value={
                                selectedTenant ? selectedTenant.tenantId : ''
                            }
                            className="anmForm"
                            fullWidth
                            onChange={(e) => {
                                if (e.target.value) {
                                    const tenant = azureTenants.find(
                                        (tnt) => tnt.tenantId === e.target.value
                                    );
                                    setSelectedTenant(tenant);
                                    onSelectTenant(tenant);
                                }
                            }}
                        >
                            {!azureTenants.length && (
                                <MenuItem
                                    key="azTenantSelectorNoTenantsFound"
                                    value={null}
                                >
                                    {t(
                                        'Provider.MicrosoftConfiguration.tenantSelector.noTenantsFound'
                                    )}
                                </MenuItem>
                            )}
                            {azureTenants.map((tenant) => (
                                <MenuItem
                                    key={tenant.tenantId}
                                    value={tenant.tenantId}
                                >
                                    {tenant.displayName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Flex>
            </FormControl>
        );
    }

    return (
        <Flex justifyContent="center" mb="24px">
            <CircularProgress />
        </Flex>
    );
};
